import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSnackbar } from 'notistack';
import {
  Box,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import ListActionBar from 'src/components/ListActionBar';
import StatusButton from 'src/components/StatusButton';
import TableActionButton from 'src/components/TableActionButton';
import TableHeadTypography from 'src/components/TableHeadTypography';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { useTagOptions } from 'src/hooks/tag';
import {
  useExportPackagedApps,
  useInfinitePackagedApps,
  useUpdatePackagedAppStatuses,
} from 'src/hooks/wdac';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { pagesText } from 'src/constants/pagesText';
import { csvExportFileName, handleCsvFileDownload } from 'src/utils/download.util';
import {
  PACKAGED_APP_STATUS,
  getPackagedAppStatusLabel,
  getPackagedAppStatusOptions,
} from 'src/utils/enums/packagedAppStatus';
import WarningIcon from 'src/components/WarningIcon';
import UpdateStatusModal from 'src/features/appStatus/UpdateStatusModal';
import {
  PACKAGED_APPS_COLUMNS,
  PACKAGED_APPS_COLUMN_GROUPS,
  PACKAGED_APPS_STATUS_CATALOG_TARGET,
  PACKAGED_APPS_STATUS_OPTIONS,
  WDAC_PACKAGED_APP_PREVIOUS_PATH,
} from './constants';

const CLOSED_STATUS_MODAL = { open: false, rows: [] };

const GROUP_DIVIDER_SX = { borderRight: '2px solid #2E3236' };
const GROUP_ROW_HEIGHT = 30;

// Names and family names need the room; the count columns only ever hold small integers.
const COLUMN_WEIGHTS = { status: 1.6, displayName: 2.6, signatureKind: 1.2 };

// An app drifts from the generated policy two ways: trusted but not in one, or in one but untrusted.
const getPolicyTrustWarning = (status, trustedAsOfVersion) => {
  if (status === PACKAGED_APP_STATUS.Trusted && !trustedAsOfVersion) {
    return 'Trusted, but not in a supplemental policy yet — create or update a policy to allow it.';
  }
  if (trustedAsOfVersion && status !== PACKAGED_APP_STATUS.Trusted) {
    return `Still allowed by the generated policy (${trustedAsOfVersion}) but no longer trusted — regenerate the policy to remove it.`;
  }
  return null;
};

const useSignatureKindOptions = signatureKinds =>
  useMemo(
    () => [
      { value: '', label: 'All Kinds' },
      ...(signatureKinds || []).filter(Boolean).map(kind => ({ value: kind, label: kind })),
    ],
    [signatureKinds],
  );

const ColumnHeader = ({ label, tooltip, sortable, sortActive, sortDirection, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const row = (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={sortable ? onSort : undefined}
      sx={{ cursor: sortable ? 'pointer' : tooltip ? 'help' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {label}
      </TableHeadTypography>
      {sortable ? (
        <Iconify
          icon={
            sortActive
              ? sortDirection === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );

  return tooltip ? (
    <Tooltip
      title={tooltip}
      arrow
      componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320 } } }}>
      <Box component="span" display="inline-flex">
        {row}
      </Box>
    </Tooltip>
  ) : (
    row
  );
};

const CountCell = ({ value }) => (
  <TableCell align="center" sx={{ py: 0.5 }}>
    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
      {Number(value || 0).toLocaleString()}
    </Typography>
  </TableCell>
);

const PackagedAppsTable = ({ filters, onFiltersChange, signatureKinds }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const { data: tagOptionsData } = useTagOptions({ search: '', enabled: true });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);

  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({ column: 'affectedDevices30Days', direction: 'desc' });

  const apiQuery = useMemo(() => {
    const params = { sortColumn: sort.column, sortDesc: sort.direction === 'desc' };
    const trimmed = searchValue.trim();
    if (trimmed) {
      params.search = trimmed;
    }
    if (filters.trustStatus) {
      params.trustStatus = filters.trustStatus;
    }
    if (filters.tagId) {
      params.customerEnvironmentTagId = filters.tagId;
    }
    if (filters.signatureKind) {
      params.signatureKind = filters.signatureKind;
    }
    return params;
  }, [filters, searchValue, sort]);

  const packagedAppsQuery = useInfinitePackagedApps({
    apiQuery,
    limit: EXPANDED_ROWS_LIMIT,
  });

  const results = useMemo(
    () => packagedAppsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [packagedAppsQuery.data],
  );
  const totalCount = packagedAppsQuery.data?.pages?.[0]?.totalCount || 0;
  const isFetchingNextPage = packagedAppsQuery.isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (packagedAppsQuery.hasNextPage) {
      packagedAppsQuery.fetchNextPage();
    }
  }, [packagedAppsQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: packagedAppsQuery.hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const columnWidths = useMemo(() => {
    const getWeight = key => COLUMN_WEIGHTS[key] ?? 1;
    const totalParts = PACKAGED_APPS_COLUMNS.reduce(
      (sum, column) => sum + getWeight(column.key),
      0,
    );
    return Object.fromEntries(
      PACKAGED_APPS_COLUMNS.map(column => [
        column.key,
        `${(getWeight(column.key) / totalParts) * 100}%`,
      ]),
    );
  }, []);

  const signatureKindOptions = useSignatureKindOptions(signatureKinds);

  const filterOptions = useMemo(
    () => [
      {
        key: 'trustStatus',
        type: 'select',
        label: 'Trust Status',
        options: [{ value: '', label: 'All statuses' }, ...getPackagedAppStatusOptions()],
      },
      {
        key: 'signatureKind',
        type: 'select',
        label: 'Signature Kind',
        options: signatureKindOptions,
      },
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All Tags' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
    ],
    [signatureKindOptions, tagOptions],
  );

  const onFilterChange = useCallback(
    nextFilters => {
      onFiltersChange({
        trustStatus: nextFilters.trustStatus || '',
        signatureKind: nextFilters.signatureKind || '',
        tagId: nextFilters.tagId || '',
      });
    },
    [onFiltersChange],
  );

  const [selectedRows, setSelectedRows] = useState(() => new Set());

  useEffect(() => {
    setSelectedRows(new Set());
  }, [filters, searchValue]);

  const toggleRowSelection = useCallback(row => {
    setSelectedRows(previous => {
      const next = new Set(previous);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    column => {
      if (!column.sortable || !column.sortKey) {
        return;
      }
      setSort(previous =>
        previous.column === column.sortKey
          ? { column: column.sortKey, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
          : { column: column.sortKey, direction: 'desc' },
      );
    },
    [setSort],
  );

  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);
  const updateStatusesMutation = useUpdatePackagedAppStatuses();

  const openEditModal = useCallback(row => setStatusModal({ open: true, rows: [row] }), []);

  const openBulkModal = useCallback(() => {
    const rows = results.filter(row => selectedRows.has(row.id));
    if (rows.length > 0) {
      setStatusModal({ open: true, rows });
    }
  }, [results, selectedRows]);

  // Rows are kept on close so the dialog body does not empty out mid transition.
  const closeStatusModal = useCallback(
    () => setStatusModal(previous => ({ ...previous, open: false })),
    [],
  );

  const saveStatusModal = useCallback(
    async (status, notes) => {
      const globalPackagedAppIds = statusModal.rows.map(row => row.id);
      if (globalPackagedAppIds.length === 0) {
        return;
      }

      try {
        await updateStatusesMutation.mutateAsync({ globalPackagedAppIds, status, notes });
        enqueueSnackbar(
          globalPackagedAppIds.length === 1
            ? 'Status updated'
            : `${globalPackagedAppIds.length.toLocaleString()} statuses updated`,
          { variant: 'success', autoHideDuration: 3000 },
        );
        closeStatusModal();
        setSelectedRows(new Set());
      } catch (error) {
        enqueueSnackbar(`Could not update status: ${error?.message || error}`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [closeStatusModal, enqueueSnackbar, statusModal.rows, updateStatusesMutation],
  );

  // The export follows the table's filters, search and sort, but not its selection or paging.
  const exportMutation = useExportPackagedApps();
  const handleExport = async () => {
    try {
      const csv = await exportMutation.mutateAsync({ apiQuery });
      handleCsvFileDownload(csv, csvExportFileName('packaged_apps_export'));
    } catch {
      enqueueSnackbar(pagesText.somethingWentWrong, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const openAppDetails = useCallback(
    row => {
      sessionStorage.setItem(
        WDAC_PACKAGED_APP_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/packaged-details/${row.id}/general`);
    },
    [navigate],
  );

  const trimmedSearch = searchValue.trim();
  const modalRow = statusModal.rows.length === 1 ? statusModal.rows[0] : null;

  const renderCell = useCallback(
    (column, row) => {
      switch (column.key) {
        case 'status': {
          // A system-signed app that is already trusted cannot be moved, so it offers neither a
          // checkbox nor an editable button.
          const isSystemTrusted =
            row.status === PACKAGED_APP_STATUS.Trusted && row.signatureKind === 'System';
          return (
            <TableCell key={`${row.id}-status`} sx={{ py: 0.5, px: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  className="hover-checkbox"
                  sx={{
                    width: 34,
                    opacity: selectedRows.has(row.id) ? 1 : 0.35,
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1,
                      '& .MuiSvgIcon-root path': { fill: '#7DFF92' },
                    },
                  }}>
                  {isSystemTrusted ? null : (
                    <Checkbox
                      size="small"
                      checked={selectedRows.has(row.id)}
                      onChange={() => toggleRowSelection(row)}
                      onClick={event => event.stopPropagation()}
                      sx={{ '& .MuiSvgIcon-root path': { fill: '#7DFF92' } }}
                    />
                  )}
                </Box>
                <StatusButton
                  status={row.status}
                  size="small"
                  disabled={isSystemTrusted}
                  sx={{ fontSize: '11px', padding: '2px 6px', minWidth: 0 }}
                  onClick={event => {
                    event.stopPropagation();
                    openEditModal(row);
                  }}>
                  {getPackagedAppStatusLabel(row.status)}
                </StatusButton>
                <WarningIcon message={getPolicyTrustWarning(row.status, row.trustedAsOfVersion)} />
                {row.notes ? (
                  <Tooltip
                    title={row.notes}
                    arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320 } } }}>
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      width={18}
                      height={18}>
                      <Image
                        src="/static/icons/notes-notepad.svg"
                        alt="Note"
                        width={16}
                        height={16}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
              </Stack>
            </TableCell>
          );
        }
        case 'displayName':
          return (
            <TableCell key={`${row.id}-displayName`} sx={{ py: 0.5, minWidth: 0 }}>
              <Stack gap="2px" justifyContent="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TableCellTypography
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: 1.25,
                    m: 0,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    maxWidth: '100%',
                  }}>
                  <HighlightedText
                    text={row.displayName}
                    searchValue={trimmedSearch}
                    fallback="—"
                  />
                </TableCellTypography>
                <TableCellTypography
                  component="div"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#9CA3AF',
                    lineHeight: 1.25,
                    m: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    maxWidth: '100%',
                  }}>
                  <HighlightedText
                    text={row.packageFamilyName}
                    searchValue={trimmedSearch}
                    fallback="—"
                  />
                </TableCellTypography>
              </Stack>
            </TableCell>
          );
        case 'signatureKind':
          return (
            <TableCell key={`${row.id}-signatureKind`} sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.signatureKind || '—'}
              </Typography>
            </TableCell>
          );
        default:
          return <CountCell key={`${row.id}-${column.key}`} value={row[column.key]} />;
      }
    },
    [openEditModal, selectedRows, toggleRowSelection, trimmedSearch],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        searchPlaceholder="Display name or package family name"
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={3}
        filterButtonCount={totalCount}
        actionArea={
          selectedRows.size > 0 ? (
            <TableActionButton variant="outlined" onClick={openBulkModal} sx={{ px: 1.5 }}>
              {`Change Status (${selectedRows.size} app${selectedRows.size === 1 ? '' : 's'})`}
            </TableActionButton>
          ) : (
            <TableActionButton
              variant="outlined"
              onClick={handleExport}
              disabled={exportMutation.isLoading}
              sx={{ px: 1.5 }}>
              {exportMutation.isLoading ? 'Exporting...' : 'Export All'}
            </TableActionButton>
          )
        }
      />

      <TableContainer roundedTop styles={{ maxHeight: 480 }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <colgroup>
            {PACKAGED_APPS_COLUMNS.map(column => (
              <col key={column.key} style={{ width: columnWidths[column.key] }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
            <TableRow>
              {PACKAGED_APPS_COLUMN_GROUPS.map((group, index) => (
                <TableCell
                  key={group.title || `group-${index}`}
                  colSpan={group.span}
                  align="center"
                  sx={{
                    top: 0,
                    height: GROUP_ROW_HEIGHT,
                    p: '0 8px',
                    ...(index < PACKAGED_APPS_COLUMN_GROUPS.length - 1 ? GROUP_DIVIDER_SX : null),
                  }}>
                  <TableHeadTypography
                    mediumScreen={mediumScreen}
                    largeScreen={largeScreen}
                    smallScreen={smallScreen}>
                    {group.title}
                  </TableHeadTypography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {PACKAGED_APPS_COLUMNS.map(column => (
                <TableCell
                  key={column.key}
                  sx={{
                    top: GROUP_ROW_HEIGHT,
                    zIndex: 3,
                    p: column.key === 'status' ? '8px' : '8px 16px',
                    verticalAlign: 'middle',
                    ...(column.groupEnd ? GROUP_DIVIDER_SX : null),
                  }}>
                  <ColumnHeader
                    label={column.label}
                    tooltip={column.tooltip}
                    sortable={!!column.sortable}
                    sortActive={sort.column === column.sortKey}
                    sortDirection={sort.direction}
                    onSort={() => handleSort(column)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {packagedAppsQuery.isLoading ? (
              <LoadingRows columnCount={PACKAGED_APPS_COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {results.map((row, index) => (
                  <TableRow
                    key={row.id}
                    ref={index === results.length - 1 ? sentryRef : null}
                    onClick={() => openAppDetails(row)}
                    sx={{ '& td': { py: 0.35 }, cursor: 'pointer' }}>
                    {PACKAGED_APPS_COLUMNS.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {isFetchingNextPage && (
                  <LoadingRows columnCount={PACKAGED_APPS_COLUMNS.length} rowCount={3} />
                )}
                {!isFetchingNextPage && results.length === 0 && (
                  <NoDataCell colSpan={PACKAGED_APPS_COLUMNS.length}>
                    No packaged apps found.
                  </NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UpdateStatusModal
        open={statusModal.open}
        onClose={closeStatusModal}
        count={statusModal.rows.length}
        noun="packaged app"
        summary={modalRow ? modalRow.displayName || modalRow.packageFamilyName : null}
        currentStatus={modalRow?.status || ''}
        currentNote={modalRow?.notes || ''}
        currentDate={modalRow?.statusUpdatedOn || null}
        statusOptions={PACKAGED_APPS_STATUS_OPTIONS}
        catalogTarget={PACKAGED_APPS_STATUS_CATALOG_TARGET}
        prefillNote
        onSave={saveStatusModal}
        loading={updateStatusesMutation.isLoading}
      />
    </Box>
  );
};

export default PackagedAppsTable;
